/**
 * "Save search" (#2291): a title (prefilled from the search's chips), an
 * optional description, and whether and how it shows on Discover. A centred
 * dialog on a desktop, a bottom sheet on a phone (CSS).
 *
 * Mounted only while open, so each opening starts from its props. It stays
 * open while the save is stored, and shows why when it is refused: the
 * server's problems in plain words, or a limit or a conflict.
 */
import { useId, useState, type FormEvent } from 'react';
import Modal from '@/components/common/Modal';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { DISCOVER_LIMITS, type DiscoverVariant } from '@/api/discoverPreferences';
import { PLACEMENTS, SHOW_AS, type Placement } from './discoverLayout';
import { outcomeMessages, type WriteOutcome } from './discoverLayoutStore';
import '../search/discoverSearch.css';

export interface SaveSearchValues {
  title: string;
  description: string;
  show: boolean;
  place: Placement;
  variant: DiscoverVariant;
}

interface Props {
  initialTitle: string;
  /** "Save as new" from an edited search, rather than a first save. */
  asNew?: boolean;
  onSave: (values: SaveSearchValues) => Promise<WriteOutcome>;
  onClose: () => void;
}

export default function SaveSearchDialog({ initialTitle, asNew = false, onSave, onClose }: Readonly<Props>) {
  const formId = useId();
  const titleId = useId();
  const descId = useId();
  const placeId = useId();
  const showAsId = useId();
  const [title, setTitle] = useState(initialTitle.slice(0, DISCOVER_LIMITS.title));
  const [description, setDescription] = useState('');
  const [show, setShow] = useState(true);
  const [place, setPlace] = useState<Placement>('bottom');
  const [variant, setVariant] = useState<DiscoverVariant>('carousel');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const trimmed = title.trim();
  const local = [
    ...(/</.test(title) ? ["Titles can't contain <."] : []),
    ...(/</.test(description) ? ["Descriptions can't contain <."] : []),
  ];
  const canSave = !busy && trimmed.length > 0 && local.length === 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setProblems([]);
    const outcome = await onSave({ title: trimmed, description: description.trim(), show, place, variant });
    if (outcome.ok) {
      onClose();
      return;
    }
    setBusy(false);
    setProblems(outcomeMessages(outcome));
  };

  const shown = [...local, ...problems];

  return (
    <Modal
      open
      onClose={onClose}
      title={asNew ? 'Save as a new search' : 'Save search'}
      size="md"
      footer={(
        <>
          <button type="button" className="aurora-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form={formId} className="aurora-btn aurora-btn-primary" disabled={!canSave}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <form id={formId} className="dsq-save" onSubmit={submit} noValidate>
        <div className="dsq-panel-field">
          <div className="dsq-save-labelrow">
            <label className="dsq-panel-label" htmlFor={titleId}>Title</label>
            <span className="dsq-counter" aria-hidden="true">{title.length}/{DISCOVER_LIMITS.title}</span>
          </div>
          <input
            id={titleId}
            className="dsq-panel-input"
            value={title}
            maxLength={DISCOVER_LIMITS.title}
            required
            autoFocus // NOSONAR typescript:S9379 accessibility-by-design: dialog autoFocus (primary control focused on open)
            aria-describedby={`${titleId}-n`}
            onChange={(e) => setTitle(e.target.value)}
          />
          <span id={`${titleId}-n`} className="sr-only">
            {DISCOVER_LIMITS.title - title.length} characters left
          </span>
        </div>

        <div className="dsq-panel-field">
          <div className="dsq-save-labelrow">
            <label className="dsq-panel-label" htmlFor={descId}>Description <span className="dsq-optional">(optional)</span></label>
            <span className="dsq-counter" aria-hidden="true">{description.length}/{DISCOVER_LIMITS.description}</span>
          </div>
          <textarea
            id={descId}
            className="dsq-panel-input dsq-textarea"
            value={description}
            maxLength={DISCOVER_LIMITS.description}
            rows={3}
            aria-describedby={`${descId}-n`}
            onChange={(e) => setDescription(e.target.value)}
          />
          <span id={`${descId}-n`} className="sr-only">
            {DISCOVER_LIMITS.description - description.length} characters left
          </span>
        </div>

        <div className="dsq-switch-row">
          <span>Show on Discover</span>
          <button
            type="button"
            role="switch"
            className="dsq-toggle"
            aria-checked={show}
            aria-label="Show on Discover"
            onClick={() => setShow((s) => !s)}
          >
            {show ? 'On' : 'Off'}
          </button>
        </div>

        <div className="dsq-panel-field">
          <span className="dsq-panel-label" id={placeId}>Place it</span>
          <SegmentedControl<Placement>
            className="dsq-seg"
            ariaLabelledby={placeId}
            value={place}
            options={PLACEMENTS}
            onChange={setPlace}
            disabled={!show}
          />
        </div>

        <div className="dsq-panel-field">
          <span className="dsq-panel-label" id={showAsId}>Show as</span>
          <SegmentedControl<DiscoverVariant>
            className="dsq-seg"
            ariaLabelledby={showAsId}
            value={variant}
            options={SHOW_AS}
            onChange={setVariant}
            disabled={!show}
          />
        </div>

        {shown.length > 0 && (
          <ul className="dsq-problems" role="alert">
            {shown.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
      </form>
    </Modal>
  );
}
